<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const emit = defineEmits(['close', 'connected']);

const { t } = useI18n();
const store = useStore();

const deviceName = ref('');
const isCreating = ref(false);

const handleDownload = () => {
  // Abrir la URL de descarga del APK de 66text
  window.open('https://bt.buho.xyz/app/download', '_blank');
};

const handleCreateDevice = async () => {
  if (!deviceName.value.trim()) {
    useAlert(
      t('CAMPAIGN.BUHOTEXT.DEVICES.CONNECT_DIALOG.NAME_REQUIRED'),
      'error'
    );
    return;
  }

  isCreating.value = true;
  try {
    await store.dispatch('buhotextCampaigns/createDevice', {
      name: deviceName.value.trim(),
    });

    useAlert(t('CAMPAIGN.BUHOTEXT.DEVICES.CONNECT_DIALOG.DEVICE_CREATED'));
    emit('connected');
    emit('close');
  } catch (error) {
    useAlert(
      t('CAMPAIGN.BUHOTEXT.DEVICES.CONNECT_DIALOG.CREATE_ERROR'),
      'error'
    );
  } finally {
    isCreating.value = false;
  }
};
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
  >
    <div
      class="w-full max-w-md m-4 bg-n-alpha-3 backdrop-blur-[100px] rounded-xl border border-n-weak shadow-xl"
    >
      <div class="p-6">
        <!-- Header -->
        <div class="flex items-start justify-between mb-6">
          <div class="flex items-center gap-3">
            <div
              class="w-12 h-12 rounded-full bg-n-purple-3 flex items-center justify-center"
            >
              <i class="i-lucide-smartphone text-2xl text-n-purple-11" />
            </div>
            <div>
              <h3 class="text-lg font-semibold text-n-slate-12">
                {{ t('CAMPAIGN.BUHOTEXT.DEVICES.CONNECT_DIALOG.TITLE') }}
              </h3>
              <p class="text-sm text-n-slate-11">
                {{
                  t(
                    'CAMPAIGN.BUHOTEXT.DEVICES.CONNECT_DIALOG.STEP_1.DESCRIPTION'
                  )
                }}
              </p>
            </div>
          </div>
          <button
            class="text-n-slate-11 hover:text-n-slate-12"
            @click="emit('close')"
          >
            <i class="i-lucide-x text-xl" />
          </button>
        </div>

        <!-- Content -->
        <div class="space-y-4">
          <!-- Nombre del dispositivo -->
          <div>
            <Input
              v-model="deviceName"
              :label="
                t('CAMPAIGN.BUHOTEXT.DEVICES.CONNECT_DIALOG.DEVICE_NAME_LABEL')
              "
              :placeholder="
                t(
                  'CAMPAIGN.BUHOTEXT.DEVICES.CONNECT_DIALOG.DEVICE_NAME_PLACEHOLDER'
                )
              "
              required
            />
          </div>

          <!-- Descargar APK -->
          <div class="text-center py-6 border-t border-n-weak">
            <div
              class="w-16 h-16 mx-auto mb-3 rounded-full bg-n-green-3 flex items-center justify-center"
            >
              <i class="i-lucide-download text-3xl text-n-green-11" />
            </div>
            <h4 class="font-semibold text-n-slate-12 mb-2">
              {{ t('CAMPAIGN.BUHOTEXT.DEVICES.CONNECT_DIALOG.STEP_1.TITLE') }}
            </h4>
            <p class="text-sm text-n-slate-11 mb-4">
              {{
                t(
                  'CAMPAIGN.BUHOTEXT.DEVICES.CONNECT_DIALOG.DOWNLOAD_DESCRIPTION'
                )
              }}
            </p>
            <Button
              :label="
                t(
                  'CAMPAIGN.BUHOTEXT.DEVICES.CONNECT_DIALOG.STEP_1.DOWNLOAD_BUTTON'
                )
              "
              icon="i-lucide-download"
              slate
              @click="handleDownload"
            />
          </div>

          <div
            class="bg-n-blue-3 border border-n-blue-6 rounded-lg p-4 text-sm"
          >
            <div class="flex gap-2">
              <i class="i-lucide-info text-n-blue-11 mt-0.5" />
              <div class="text-n-blue-11">
                <p class="font-medium mb-1">
                  {{ t('CAMPAIGN.BUHOTEXT.DEVICES.CONNECT_DIALOG.INFO_TITLE') }}
                </p>
                <p>
                  {{
                    t('CAMPAIGN.BUHOTEXT.DEVICES.CONNECT_DIALOG.INFO_MESSAGE')
                  }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex gap-3 justify-end mt-6">
          <Button
            :label="t('CAMPAIGN.BUHOTEXT.DEVICES.CONNECT_DIALOG.CLOSE')"
            slate
            @click="emit('close')"
          />
          <Button
            :label="t('CAMPAIGN.BUHOTEXT.DEVICES.CONNECT_DIALOG.CREATE_BUTTON')"
            icon="i-lucide-plus"
            :loading="isCreating"
            @click="handleCreateDevice"
          />
        </div>
      </div>
    </div>
  </div>
</template>
